/**
 * Geofence panel widget — COP-style list that uses the existing web map
 * for rendering and drawing. Geofences are stored locally in the widget config.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CancelIcon,
  MyLocation as LocationIcon,
} from '@mui/icons-material';
import { useWebMapContext } from './WebMapContext';
import type { LocalGeofence, GeofenceWidgetConfig } from '../builder/types';

const FENCE_COLORS = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

function polygonArea(coords: number[][]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371e3;
  let area = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const [lng1, lat1] = coords[i];
    const [lng2, lat2] = coords[i + 1];
    area += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((area * R * R) / 2);
}

function formatArea(sqm: number): string {
  if (sqm < 10_000) return `${Math.round(sqm)} m²`;
  if (sqm < 1_000_000) return `${(sqm / 10_000).toFixed(2)} ha`;
  return `${(sqm / 1_000_000).toFixed(2)} km²`;
}

/** Ray-casting point-in-polygon for a single ring. */
function pointInPolygon(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function countFeaturesInFence(features: GeoJSON.Feature[], fence: LocalGeofence): number {
  const ring = fence.geometry.coordinates[0];
  let count = 0;
  for (const f of features) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === 'Point') {
      if (pointInPolygon(g.coordinates[0], g.coordinates[1], ring)) count++;
    }
  }
  return count;
}

function getEntitiesInFence(features: GeoJSON.Feature[], fence: LocalGeofence): string[] {
  const ring = fence.geometry.coordinates[0];
  const entities: string[] = [];
  for (const f of features) {
    const g = f.geometry;
    if (!g || g.type !== 'Point') continue;
    if (pointInPolygon(g.coordinates[0], g.coordinates[1], ring)) {
      const name = f.properties?.entity_id ?? f.properties?.device_id ?? f.properties?.name ?? f.properties?.id;
      if (name != null) entities.push(String(name));
    }
  }
  return entities;
}

export interface GeofenceWidgetProps {
  widgetConfig?: GeofenceWidgetConfig;
  editMode?: boolean;
  onUpdateConfig?: (config: GeofenceWidgetConfig) => void;
}

const GeofenceWidget: React.FC<GeofenceWidgetProps> = ({ widgetConfig, editMode: _editMode, onUpdateConfig }) => {
  const webMapCtx = useWebMapContext();
  const config = widgetConfig ?? {};
  const [geofences, setGeofences] = useState<LocalGeofence[]>(config.geofences ?? []);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [pendingGeometry, setPendingGeometry] = useState<{ type: 'Polygon'; coordinates: number[][][] } | null>(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    setGeofences(config.geofences ?? []);
  }, [config.geofences?.length]);

  // Push visible geofences to the web map context for rendering
  useEffect(() => {
    if (!webMapCtx) return;
    const visible = geofences.filter((g) => !hiddenIds.has(g.id));
    webMapCtx.setGeofenceOverlay(visible);
  }, [geofences, hiddenIds, webMapCtx]);

  const persist = useCallback(
    (fences: LocalGeofence[]) => {
      setGeofences(fences);
      onUpdateConfig?.({ ...config, geofences: fences });
    },
    [config, onUpdateConfig],
  );

  // Gather all map features for inside-count
  const allMapFeatures = useMemo((): GeoJSON.Feature[] => {
    if (!webMapCtx) return [];
    const all: GeoJSON.Feature[] = [];
    for (const lyr of webMapCtx.layers) {
      all.push(...webMapCtx.getFeaturesForLayer(lyr.id));
    }
    return all;
  }, [webMapCtx]);

  // Register the draw callback on the context
  useEffect(() => {
    if (!webMapCtx) return;
    webMapCtx.onGeofenceDrawComplete.current = (geometry) => {
      setPendingGeometry(geometry);
    };
    return () => {
      webMapCtx.onGeofenceDrawComplete.current = null;
    };
  }, [webMapCtx]);

  const startDraw = useCallback(() => {
    webMapCtx?.setGeofenceDrawMode(true);
  }, [webMapCtx]);

  const handleSaveFence = useCallback(() => {
    if (!pendingGeometry) return;
    const name = newName.trim() || `Geofence ${geofences.length + 1}`;
    const fence: LocalGeofence = {
      id: `gf_${Date.now()}`,
      name,
      geometry: pendingGeometry,
      color: FENCE_COLORS[geofences.length % FENCE_COLORS.length],
      createdAt: new Date().toISOString(),
    };
    persist([...geofences, fence]);
    setPendingGeometry(null);
    setNewName('');
  }, [pendingGeometry, newName, geofences, persist]);

  const handleCancelDraw = useCallback(() => {
    setPendingGeometry(null);
    webMapCtx?.setGeofenceDrawMode(false);
    setNewName('');
  }, [webMapCtx]);

  const handleDelete = useCallback(
    (id: string) => {
      persist(geofences.filter((g) => g.id !== id));
      if (webMapCtx?.geofenceSelectedId === id) webMapCtx.setGeofenceSelectedId(null);
    },
    [geofences, persist, webMapCtx],
  );

  const toggleVisibility = useCallback((id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectFence = useCallback(
    (id: string) => {
      const next = webMapCtx?.geofenceSelectedId === id ? null : id;
      webMapCtx?.setGeofenceSelectedId(next);
    },
    [webMapCtx],
  );

  const isDrawing = webMapCtx?.geofenceDrawMode ?? false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <Card
          variant="outlined"
          sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'divider', px: 2, py: 1.5, borderRadius: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <LocationIcon sx={{ fontSize: 28, color: 'success.main' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Geofences</Typography>
              <Typography variant="caption" color="text.secondary">
                {geofences.length} configured
              </Typography>
            </Box>
          </Stack>

          {!isDrawing && !pendingGeometry && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={startDraw}
              disabled={!webMapCtx}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Draw New Geofence
            </Button>
          )}

          {!webMapCtx && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              Add a Web Map widget to enable drawing.
            </Typography>
          )}
        </Card>
      </Box>

      {/* Pending save prompt */}
      {pendingGeometry && (
        <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
          <Card variant="outlined" sx={{ bgcolor: 'rgba(244,67,54,0.08)', borderColor: 'error.main', px: 2, py: 1.5, borderRadius: 2 }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
              Name this geofence ({formatArea(polygonArea(pendingGeometry.coordinates[0]))})
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <TextField
                size="small"
                placeholder={`Geofence ${geofences.length + 1}`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveFence()}
                autoFocus
                fullWidth
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
              />
              <IconButton size="small" onClick={handleSaveFence} color="success">
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancelDraw}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Card>
        </Box>
      )}

      {/* Geofence list */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 1 }}>
        <Stack spacing={1}>
          {geofences.length === 0 && !pendingGeometry && (
            <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
              No geofences configured. Draw one on the map to get started.
            </Typography>
          )}

          {geofences.map((fence) => {
            const insideCount = countFeaturesInFence(allMapFeatures, fence);
            const entities = insideCount > 0 ? getEntitiesInFence(allMapFeatures, fence) : [];
            const isSelected = webMapCtx?.geofenceSelectedId === fence.id;
            const isHidden = hiddenIds.has(fence.id);

            return (
              <Card
                key={fence.id}
                variant="outlined"
                onClick={() => selectFence(fence.id)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: isSelected ? 'rgba(255,255,255,0.06)' : 'transparent',
                  borderColor: isSelected ? fence.color : 'divider',
                  borderWidth: isSelected ? 2 : 1,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  opacity: isHidden ? 0.5 : 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {fence.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${insideCount} inside`}
                        color={insideCount > 0 ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      Created: {new Date(fence.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      {formatArea(polygonArea(fence.geometry.coordinates[0]))}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, ml: 1 }}>
                    <Tooltip title={isHidden ? 'Show on map' : 'Hide from map'}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleVisibility(fence.id); }}
                      >
                        {isHidden ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete geofence">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDelete(fence.id); }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Entity chips */}
                {entities.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                    {entities.slice(0, 3).map((name) => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ height: 22, fontSize: '0.65rem' }}
                      />
                    ))}
                    {entities.length > 3 && (
                      <Chip
                        label={`+${entities.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                )}
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default GeofenceWidget;
